import React, { FC, useEffect, useMemo, useState } from "react";
import BackgroundSection from "components/BackgroundSection/BackgroundSection";
import BgGlassmorphism from "components/BgGlassmorphism/BgGlassmorphism";
import SectionHeroArchivePage from "components/SectionHeroArchivePage/SectionHeroArchivePage";
import SectionSliderNewCategories from "components/SectionSliderNewCategories/SectionSliderNewCategories";
import MultiStockCard from "components/Quasi/MultiStockCard/MultiStockCard";
import SearchResultSkeleton from "components/SearchResultCard/SearchResultSkeleton";
import Heading2 from "components/Heading/Heading2";
import TabFilters from "../ListingStayPage/TabFilters";
import ExperiencesFilter from "components/ComponentsToMove/ExperiencesFilter";
import { Helmet } from "react-helmet";
import { useLazyGetAvailablePropertiesByDateQuery } from "application/features/property/propertyApiSlice";
import { useLazyFilterExperienceQuery } from "application/features/experience/experienceApiSlice";
import { useLazyGetFilterHolidayPackagesQuery } from "application/features/holidayPackage/holidayPackageApiSlice";
import { useSelector } from "react-redux";
import {
  selectCheckIn,
  selectCheckOut,
  selectCity,
  selectCountry,
  selectGuests,
  selectLatitude,
  selectLongitude,
  selectBounds,
  selectMood,
  selectRegion,
  selectSearchResults,
  selectSearchType,
  selectTrend,
  setSearchResults,
  setSearchType
} from "application/features/search/searchSlice";
import { StockNumericType, StockEnum, MoodEnum } from "data/enums";
import { parseRouteParamToStockNumericType, parseStringToStockNumericType, parseNumericToStockEnum, stockDetailPath } from "data/stock_types";
import { useDispatch } from "react-redux";
import useWindowSize from "application/hooks/useWindowResize";
import { useLocation, useParams } from "react-router-dom";
import Lottie from 'react-lottie';
import * as EmptyAnimation from "../../images/lotties/empty.json";
import { useTranslation } from "react-i18next";
import { useGetHolidayPackageHighlightsQuery } from "application/features/holidayPackage/holidayPackageApiSlice";
import { DEFAULT_ITALY_COORDINATES } from "data/constants";
import {
  AdvancedMarker, 
  APIProvider, 
  Map,
  useMap,
} from '@vis.gl/react-google-maps';
import { GOOGLE_MAPS_API_KEY } from "data/constants";


export interface ListingStayMapPageProps {
  className?: string;
}

interface MapPoint {
  lat: number;
  lng: number;
}

interface MapMarkerItem {
  key: string;
  lat: number;
  lng: number;
}

interface MapViewportControllerProps {
  points: MapPoint[];
  bbox?: {
    north?: number;
    south?: number;
    east?: number;
    west?: number;
  };
  fallbackCenter: MapPoint;
  fallbackRadiusKm: number;
  singleResultMaxZoom?: number;
}

const MapViewportController: FC<MapViewportControllerProps> = ({
  points,
  bbox,
  fallbackCenter,
  fallbackRadiusKm,
  singleResultMaxZoom = 12,
}) => {
  const map = useMap();

  useEffect(() => {
    const googleMaps = (window as any)?.google?.maps;
    if (!map || !googleMaps) return;

    const fitOptions = { top: 72, right: 72, bottom: 72, left: 72 };
    const bounds = new googleMaps.LatLngBounds();

    if (points.length === 1) {
      map.setCenter(points[0]);
      const currentZoom = map.getZoom();
      if (currentZoom == null || currentZoom > singleResultMaxZoom) {
        map.setZoom(singleResultMaxZoom);
      }
      return;
    }

    if (points.length > 1) {
      points.forEach((point) => bounds.extend(point));
      map.fitBounds(bounds, fitOptions);
      return;
    }

    const hasBBox =
      Number.isFinite(bbox?.north) &&
      Number.isFinite(bbox?.south) &&
      Number.isFinite(bbox?.east) &&
      Number.isFinite(bbox?.west);

    if (hasBBox) {
      bounds.extend({ lat: Number(bbox?.south), lng: Number(bbox?.west) });
      bounds.extend({ lat: Number(bbox?.north), lng: Number(bbox?.east) });
      map.fitBounds(bounds, fitOptions);
      return;
    }

    if (!Number.isFinite(fallbackCenter.lat) || !Number.isFinite(fallbackCenter.lng)) {
      return;
    }

    const latDelta = fallbackRadiusKm / 111;
    const cosLat = Math.max(Math.cos((fallbackCenter.lat * Math.PI) / 180), 0.01);
    const lngDelta = fallbackRadiusKm / (111 * cosLat);

    bounds.extend({ lat: fallbackCenter.lat - latDelta, lng: fallbackCenter.lng - lngDelta });
    bounds.extend({ lat: fallbackCenter.lat + latDelta, lng: fallbackCenter.lng + lngDelta });
    map.fitBounds(bounds, fitOptions);
  }, [map, points, bbox, fallbackCenter, fallbackRadiusKm, singleResultMaxZoom]);

  return null;
};

const SearchPage: FC<ListingStayMapPageProps> = ({
  className = "",
}) => {

  const { t, ready } = useTranslation("translation");
  const { categorySlug, defaultType: legacyDefaultType } = useParams();
  // New SEO routes use `/:categorySlug` (e.g. /pacchetti-viaggio); the legacy
  // `/:defaultType/search` form is still supported for backward compatibility.
  const defaultType = categorySlug ?? legacyDefaultType;

  // 
  const [showFullMapFixed, setShowFullMapFixed] = useState(false);

  // Redux selectors
  const mood = useSelector(selectMood);
  const city = useSelector(selectCity);
  const region = useSelector(selectRegion);
  const check_in = useSelector(selectCheckIn);
  const check_out = useSelector(selectCheckOut);
  const guests = useSelector(selectGuests);
  const latitude = useSelector(selectLatitude);
  const longitude = useSelector(selectLongitude);
  const bounds = useSelector(selectBounds);
  const searchType = useSelector(selectSearchType);
  const trend = useSelector(selectTrend);
  const country = useSelector(selectCountry);
  const searchResults = useSelector(selectSearchResults);

  const location = useLocation();
  const useQuery = () => {
    return new URLSearchParams(location.search);
  };
  const query = useQuery();

  // Redux selectors
  const dispatch = useDispatch();
  const stockType: StockNumericType = parseRouteParamToStockNumericType(defaultType) ?? StockNumericType.STAY;

  // Get featured holiday packages for type 2 (holiday packages)
  const { data: highlightsData } = useGetHolidayPackageHighlightsQuery({}, {
    skip: stockType !== StockNumericType.HOLIDAY_PACKAGE
  });

  // Query parameters
  const city_q = query.get('city');
  const lat_q = query.get('latitude');
  const lng_q = query.get('longitude');
  const region_q = query.get('region');
  const country_q = query.get('country');
  const bboxNorth_q = query.get('bboxNorth');
  const bboxSouth_q = query.get('bboxSouth');
  const bboxEast_q = query.get('bboxEast');
  const bboxWest_q = query.get('bboxWest');
  const check_in_q = query.get('check_in');
  const check_out_q = query.get('check_out');
  const guests_q = query.get('guests');
  const mood_q = query.get('mood');
  const searchType_q = query.get('searchType');
  const trend_q = query.get('trend');

  const parseCoord = (value: string | number | null | undefined) => {
    if (value === null || value === undefined || value === "") return undefined;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  };

  const bboxNorth = parseCoord(bboxNorth_q);
  const bboxSouth = parseCoord(bboxSouth_q);
  const bboxEast = parseCoord(bboxEast_q);
  const bboxWest = parseCoord(bboxWest_q);
  const hasBBox = [bboxNorth, bboxSouth, bboxEast, bboxWest].every((v) => v !== undefined);

  const bboxCenterLat = hasBBox ? ((bboxNorth as number) + (bboxSouth as number)) / 2 : undefined;
  const bboxCenterLng = hasBBox ? ((bboxEast as number) + (bboxWest as number)) / 2 : undefined;

  // State variables
  const searchResultRef = React.useRef<HTMLDivElement>(null);
  const fallbackCenter: MapPoint = useMemo(() => ({
    lat: parseCoord(lat_q) || parseCoord(latitude) || bboxCenterLat || DEFAULT_ITALY_COORDINATES.latitude,
    lng: parseCoord(lng_q) || parseCoord(longitude) || bboxCenterLng || DEFAULT_ITALY_COORDINATES.longitude,
  }), [lat_q, latitude, bboxCenterLat, lng_q, longitude, bboxCenterLng]);

  const mapMarkers: MapMarkerItem[] = useMemo(() => {
    if (!Array.isArray(searchResults)) return [];
    console.log("Calculating map markers from search results...", searchResults);
    return searchResults
      .map((item: any) => {
        const hasKnownCardType = !!(item?.property_uuid || item?.experience_uuid || item?.package_uuid);

        if (!hasKnownCardType || item?.in_revision) {
          return null;
        }

        if (
          !item?.latitude ||
          !item?.longitude ||
          (Array.isArray(item.latitude) && item.latitude.length === 0) ||
          (Array.isArray(item.longitude) && item.longitude.length === 0)
        ) {
          return null;
        }

        const lat = Array.isArray(item.latitude) ? Number(item.latitude[0]) : Number(item.latitude);
        const lng = Array.isArray(item.longitude) ? Number(item.longitude[0]) : Number(item.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

        return {
          key: `${item.property_uuid || item.experience_uuid || item.package_uuid}-${lat}-${lng}`,
          lat,
          lng,
        };
      })
      .filter(Boolean) as MapMarkerItem[];
  }, [searchResults]);

  const mapPoints: MapPoint[] = useMemo(
    () => mapMarkers.map(({ lat, lng }) => ({ lat, lng })),
    [mapMarkers]
  );

  const fallbackRadiusKm = searchType === StockNumericType.EXPERIENCE || searchType === StockEnum.EXPERIENCE
    ? 100
    : 25;

  useEffect(() => {
    console.log("Setting search type from URL or default...", stockType, searchType_q);
    const parsedQueryType: StockNumericType = searchType_q ? parseStringToStockNumericType(searchType_q) : stockType;
    if (parsedQueryType !== stockType) {
      dispatch(setSearchType(searchType_q));
    } else {
      dispatch(setSearchType(parseNumericToStockEnum(stockType)))
    }
  }, [stockType, searchType_q]);

  const [
    filterExperiences,
    {
      isLoading: isLoadingExperiences,
      isFetching: isFetchingExperiences,
      isSuccess: isSuccessExperiences
    }
  ] = useLazyFilterExperienceQuery({});

  const [
    filterHolidayPackages,
    {
      isLoading: isLoadingHolidayPackages,
      isFetching: isFetchingHolidayPackages,
      isSuccess: isSuccessHolidayPackages
    }
  ] = useLazyGetFilterHolidayPackagesQuery({});

  const [
    searchProperties,
    {
      isLoading: isLoadingProperties,
      isFetching: isFetchingProperties,
      isSuccess: isSuccessProperties
    }
  ] = useLazyGetAvailablePropertiesByDateQuery({});

  const [isSuccess, setIsSuccess] = useState(false);
  const [showLoader, setShowLoader] = useState(false);

  // State for combined mood search (all listing types)
  const [isCombinedMoodSearch, setIsCombinedMoodSearch] = useState(false);
  const [moodSearchResults, setMoodSearchResults] = useState({
    stays: [],
    experiences: [],
    packages: []
  });

  const { width } = useWindowSize();
  const isSmallDevice = width < 768;

  useEffect(() => {
    if (!isSmallDevice) return;

    document.body.style.overflow = showFullMapFixed ? "hidden" : "";

    return () => {
      document.body.style.overflow = "";
    };
  }, [isSmallDevice, showFullMapFixed]);

  const handleFilter = async (selectedType: any, lat: any, lng: any, radius: number, rangePrices: any, selectedMoment: any) => {
    try {
      const response = await filterExperiences({ type: selectedType, latitude: lat, longitude: lng, radius, price: rangePrices, moment: selectedMoment }).unwrap();
      dispatch(setSearchResults(response));
    } catch (e) {
      console.log(e);
    }
  };

  const renderSearchResults = (isMobileView: boolean) => {
    const cardOrientation = isMobileView ? "vertical" : "vertical";

    const isInReview = (item: any) => item.in_revision;

    // function to render a single card
    const renderCard = (item: any) => {
      if (isInReview(item)) return null;
      if (item.property_uuid) {
        return (
          <MultiStockCard
            key={item.property_uuid}
            id={item.property_uuid}
            title={item.name}
            address={item.address}
            cover={item.cover}
            images={[item.cover, ...(item.gallery_images || [])].filter((url: any) => url)}
            annotation={item.property_type}
            href={stockDetailPath(StockNumericType.STAY, item.slug || item.property_uuid)}
            price={item.weekday_price}
            mood={item.mood as MoodEnum}
            orientation={cardOrientation}
          />
        );
      } else if (item.experience_uuid) {
        return (
          <MultiStockCard
            key={item.experience_uuid}
            id={item.experience_uuid}
            title={item.name}
            address={item.address}
            cover={item.cover}
            images={[item.cover, ...(item.gallery_images || [])].filter((url: any) => url)}
            annotation={item.type}
            href={stockDetailPath(StockNumericType.EXPERIENCE, item.slug || item.experience_uuid)}
            mood={item.mood as MoodEnum}
            price={item.price}
            orientation={cardOrientation}
          />
        );
      } else if (item.package_uuid) {
        return (
          <MultiStockCard
            key={item.package_uuid}
            id={item.package_uuid}
            title={item.name}
            address={[city_q || city, region_q || region].filter(Boolean).join(", ")}
            description={item.description}
            cover={item.cover_image}
            images={[item.cover_image || item.cover, ...(item.gallery_images || [])].filter((url: any) => url)}
            annotation="Pacchetto Vacanze"
            href={stockDetailPath(StockNumericType.HOLIDAY_PACKAGE, item.slug || item.package_uuid)}
            mood={item.mood as MoodEnum}
            price={item.price}
            orientation={cardOrientation}
          />
        );
      } else {
        return null;
      }
    };

    //function to render a section
    const renderSection = (title: string, items: any[]) => {
      if (!items || items.length === 0) return null;

      return (
        <div className="mb-12">
          <div className="flex items-center mb-4">
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
              {title} ({items.length} {items.length === 1 ? 'risultato' : 'risultati'})
            </h2>
          </div>
          <div className="grid grid-cols-1 gap-4 md:gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {items.map(renderCard)}
          </div>
        </div>
      );
    };

    return (
      <div className="flex flex-col min-h-screen">

        {/* Combined Mood Search - Header */}
        {
          isCombinedMoodSearch && (
            <div className="lg:pl-[2rem] mb-8 lg:mb-11">
              {/* Mood search header */}
              <div className="mb-6">
                <h1 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-1">
                  Risultati per mood: {mood_q && t(`translation:components.moods.${mood_q}.label`)}
                </h1>
                <p className="text-sm text-neutral-600 dark:text-neutral-400">
                  Esplora tutta l'offerta disponibile in base al tuo stile
                </p>
              </div>
            </div>
          )
        }

        {/* Regular Search - Header */}
        {
          !isCombinedMoodSearch && (
            <div className="lg:pl-[2rem] mb-8 lg:mb-11">
              <Heading2
                city={city_q || city || ""}
                region={region_q || region || ""}
                check_in={check_in_q || check_in || ""}
                check_out={check_out_q || check_out || ""}
                guests={guests_q ? parseInt(guests_q) : guests ? guests : 1}
                results={searchResults?.length}
              />
              <div className="mb-8 lg:mb-1">
                {searchType === StockNumericType.EXPERIENCE && (
                  <ExperiencesFilter
                    latitude={latitude}
                    longitude={longitude}
                    handleFilter={handleFilter}
                  />
                )}
                {searchType === StockNumericType.STAY && (
                  <TabFilters />
                )}
                {searchType === StockNumericType.HOLIDAY_PACKAGE && (
                  <TabFilters />
                )}
              </div>
            </div>
          )
        }

        
        <div className="relative flex">
          {/* Combined Mood Search - Show results by category */}
          {
            isCombinedMoodSearch && (
              <div className="lg:pl-[2rem]">
                {
                  showLoader ? (
                    <div className="grid grid-cols-1 gap-4 md:gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                      {Array.from({ length: 8 }).map((_, index) => (
                        <SearchResultSkeleton key={`skeleton-${index}`} id={index} />
                      ))}
                    </div>
                  ) : (
                    <>
                      {renderSection("Alloggi", moodSearchResults.stays)}
                      {renderSection("Esperienze", moodSearchResults.experiences)}
                      {renderSection("Pacchetti", moodSearchResults.packages)}

                      {/* No results at all */}
                      {(!moodSearchResults.stays || moodSearchResults.stays.length === 0) &&
                      (!moodSearchResults.experiences || moodSearchResults.experiences.length === 0) &&
                      (!moodSearchResults.packages || moodSearchResults.packages.length === 0) && (
                        <div className="text-center py-12">
                          <p className="text-gray-500 text-lg">Nessun risultato trovato per questo mood</p>
                        </div>
                      )}
                    </>
                  )
                }
              </div>
            )
          }
        
          {
            !isCombinedMoodSearch && (
              <>
                {/* Featured Holiday Packages Section - Only show for holiday packages search */}
                {
                  stockType === StockNumericType.HOLIDAY_PACKAGE && Array.isArray(highlightsData) && highlightsData.length > 0 && (
                    <div className="mb-12">
                      <div className="text-center mb-8">
                        <h2 className="text-2xl font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
                          {t("translation:home.featuredPackages.title")}
                        </h2>
                        <p className="text-neutral-600 dark:text-neutral-400">
                          {t("translation:home.featuredPackages.description")}
                        </p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {
                        
                          highlightsData
                            .filter((pkg: any) => !pkg.in_review)
                            .slice(0, 8)
                            .map((pkg: any) => (
                            <MultiStockCard
                              key={pkg.package_uuid}
                              id={pkg.package_uuid}
                              title={pkg.name}
                              cover={pkg.cover_image || pkg.cover || ""}
                              images={[pkg.cover_image || pkg.cover, ...(pkg.gallery_images || [])].filter(Boolean)}
                              annotation="Pacchetto Vacanze"
                              href={stockDetailPath(StockNumericType.HOLIDAY_PACKAGE, pkg.slug || pkg.package_uuid)}
                              mood={pkg.mood as MoodEnum}
                              price={pkg.price}
                            />
                          ))
                        }
                      </div>
                    </div>
                  )
                }


              <div className="relative flex">
                <div className={`min-h-screen w-full flex-shrink-0 xl:px-8 ${!isMobileView ? "xl:w-[780px] 2xl:w-[880px]" : ""}`}>
                  <div className={`grid grid-cols-1 gap-6 md:gap-8 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2`}>
                    {showLoader ? (
                      Array.from({ length: 6 }).map((_, index) => (
                        <SearchResultSkeleton
                          key={`skeleton-${index}`}
                          id={index}
                        />
                      ))
                    ) : !searchResults || searchResults.length === 0 ? (
                      <div className="col-span-1 md:col-span-2 lg:col-span-2 xl:col-span-2 text-center">
                        <p className="text-gray-500">Nessun risultato trovato</p>
                      </div>
                    ) : (
                      searchResults.map(renderCard)
                    )}
                  </div>
                </div>
              </div>
            </>
          )}

          {!isSmallDevice && (
            <div className="relative flex-1 hidden xl:block">
              {
                fallbackCenter.lat !== 0 && fallbackCenter.lng !== 0 && (
                  <div className="fixed xl:sticky top-0 xl:top-[88px] left-0 w-full h-full xl:h-[calc(100vh-88px)] rounded-md overflow-hidden">
                    <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
                      <Map
                        defaultCenter={{
                          lat: fallbackCenter.lat,
                          lng: fallbackCenter.lng,
                        }}
                        defaultZoom={10}
                        mapId="map-listing"
                      >
                        {mapMarkers.map((marker) => (
                          <AdvancedMarker
                            key={marker.key}
                            position={{ lat: marker.lat, lng: marker.lng }}
                          />
                        ))}
                        <MapViewportController
                          points={mapPoints}
                          bbox={{
                            north: bboxNorth,
                            south: bboxSouth,
                            east: bboxEast,
                            west: bboxWest,
                          }}
                          fallbackCenter={fallbackCenter}
                          fallbackRadiusKm={fallbackRadiusKm}
                        />
                      </Map>
                    </APIProvider>
                  </div>
                )
              }
            </div>
          )}

          {isSmallDevice && !showFullMapFixed && (
            <div
              className="flex items-center justify-center fixed bottom-[4rem] left-1/2 transform -translate-x-1/2 px-6 py-2 bg-neutral-900 text-white shadow-2xl rounded-full z-30 space-x-3 text-sm cursor-pointer"
              onClick={() => setShowFullMapFixed(true)}
            >
              <i className="text-lg las la-map"></i>
              <span>Mappa</span>
            </div>
          )}

          {isSmallDevice && showFullMapFixed && fallbackCenter.lat !== 0 && fallbackCenter.lng !== 0 && (
            <div className="fixed inset-0 z-40 bg-white">
              <div
                className="flex items-center justify-center fixed bottom-[4rem] left-1/2 transform -translate-x-1/2 px-6 py-2 bg-neutral-900 text-white shadow-2xl rounded-full z-50 space-x-3 text-sm cursor-pointer"
                onClick={() => setShowFullMapFixed(false)}
              >
                <i className="text-lg las la-times"></i>
                <span>Chiudi mappa</span>
              </div>
              <div className="absolute inset-0 overflow-hidden">
                <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
                  <Map
                    defaultCenter={{
                      lat: fallbackCenter.lat,
                      lng: fallbackCenter.lng,
                    }}
                    defaultZoom={10}
                    mapId="map-listing"
                  >
                    {mapMarkers.map((marker) => (
                      <AdvancedMarker
                        key={marker.key}
                        position={{ lat: marker.lat, lng: marker.lng }}
                      />
                    ))}
                    <MapViewportController
                      points={mapPoints}
                      bbox={{
                        north: bboxNorth,
                        south: bboxSouth,
                        east: bboxEast,
                        west: bboxWest,
                      }}
                      fallbackCenter={fallbackCenter}
                      fallbackRadiusKm={fallbackRadiusKm}
                    />
                  </Map>
                </APIProvider>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const handleSearch = async () => {
    // console.log("AutoSearch: Handling search...", searchType_q);
    const hasCoordinates = !!lat_q && !!lng_q;
    // Skip if neither coordinates nor a bbox are available
    if (!searchType_q || (!hasCoordinates && !hasBBox)) {
      console.warn("AutoSearch: searchType_q and either coordinates or bbox must be defined");
      return;
    }
    try {
      if (searchType_q === StockEnum.EXPERIENCE) {
        const response = await filterExperiences({
          latitude: lat_q || latitude,
          longitude: lng_q || longitude,
          bboxNorth,
          bboxSouth,
          bboxEast,
          bboxWest,
          region: region_q,
          check_in: check_in_q,
          check_out: check_out_q,
          country: country_q,
          guests: guests_q,
          mood: mood_q,
          radius: 100_000
        }).unwrap();
        dispatch(setSearchResults(response));
      } else if (searchType_q === StockEnum.STAY) {
        const response = await searchProperties({
          latitude: lat_q || latitude,
          longitude: lng_q || longitude,
          bboxNorth,
          bboxSouth,
          bboxEast,
          bboxWest,
          region: region_q,
          country: country_q,
          check_in: check_in_q,
          check_out: check_out_q,
          guests: guests_q,
          mood: mood_q,
          trend: trend_q
        }).unwrap();
        dispatch(setSearchResults(response));
        window.scrollTo(0, 0)
      } else if (searchType_q === StockEnum.HOLIDAY_PACKAGE) {
        const response = await filterHolidayPackages({
          latitude: lat_q || latitude,
          longitude: lng_q || longitude,
          bboxNorth,
          bboxSouth,
          bboxEast,
          bboxWest,
          region: region_q,
          country: country_q,
          check_in: check_in_q,
          check_out: check_out_q,
          guests: guests_q,
          mood: mood_q,
          trend: trend_q
        }).unwrap();
        dispatch(setSearchResults(response));
      } else {
        console.log("AutoSearch: Unknown search type");
      }
    } catch (error) {
      console.error("Failed to search properties", error);
    }
  }

  const handleSearchManual = async () => {
    console.log("Handling search...", searchType);
    try {
      if (searchType === StockEnum.EXPERIENCE || searchType === StockNumericType.EXPERIENCE) {
        const response = await filterExperiences({
          latitude: latitude,
          longitude: longitude,
          bboxNorth: bounds?.north,
          bboxSouth: bounds?.south,
          bboxEast: bounds?.east,
          bboxWest: bounds?.west,
          check_in: check_in,
          check_out: check_out,
          guests: guests,
          mood: mood,
          region: region,
          country: country,
          radius: 100
        }).unwrap();
        dispatch(setSearchResults(response));
      } else if (searchType === StockEnum.STAY || searchType === StockNumericType.STAY) {
        const response = await searchProperties({
          latitude: latitude,
          longitude: longitude,
          bboxNorth: bounds?.north,
          bboxSouth: bounds?.south,
          bboxEast: bounds?.east,
          bboxWest: bounds?.west,
          check_in: check_in,
          check_out: check_out,
          guests: guests,
          mood: mood,
          region: region,
          country: country,
          trend: trend,
        }).unwrap();
        dispatch(setSearchResults(response));
      } else if (searchType === StockEnum.HOLIDAY_PACKAGE || searchType === StockNumericType.HOLIDAY_PACKAGE) {
        console.log("Searching holiday packages...");
        const response = await filterHolidayPackages({
          latitude: lat_q || latitude,
          longitude: lng_q || longitude,
          bboxNorth,
          bboxSouth,
          bboxEast,
          bboxWest,
          region: region_q,
          country: country_q,
          check_in: check_in_q,
          check_out: check_out_q,
          guests: guests_q,
          mood: mood_q,
          trend: trend_q
        }).unwrap();
        dispatch(setSearchResults(response));
      } else {
        console.error("Unknown search type", searchType);
      }
      if (searchResultRef.current) {
        searchResultRef.current.scrollIntoView({ behavior: "smooth" });
      }

    } catch (error) {
      console.error("Failed to search properties", error);
    }
  }

  const handleCombinedMoodSearch = async () => {
    const isMoodOnly = mood_q && !lat_q && !lng_q && searchType_q === 'all';

    if (!isMoodOnly || !mood_q) return;

    const searchLat = DEFAULT_ITALY_COORDINATES.latitude;
    const searchLng = DEFAULT_ITALY_COORDINATES.longitude;
    const searchCountry = DEFAULT_ITALY_COORDINATES.country;
    const searchRadius = DEFAULT_ITALY_COORDINATES.radius;

    setIsCombinedMoodSearch(true);
    setShowLoader(true);

    try {
      const [staysResp, expResp, pkgResp] = await Promise.allSettled([
        searchProperties({
          latitude: searchLat,
          longitude: searchLng,
          country: searchCountry,
          mood: mood_q,
        }).unwrap(),
        filterExperiences({
          latitude: searchLat,
          longitude: searchLng,
          country: searchCountry,
          mood: mood_q,
          radius: searchRadius,
        }).unwrap(),
        filterHolidayPackages({
          latitude: searchLat,
          longitude: searchLng,
          country: searchCountry,
          mood: mood_q,
        }).unwrap(),
      ]);

      setMoodSearchResults({
        stays: staysResp.status === 'fulfilled' ? staysResp.value : [],
        experiences: expResp.status === 'fulfilled' ? expResp.value : [],
        packages: pkgResp.status === 'fulfilled' ? pkgResp.value : [],
      });
      setIsSuccess(true);
    } catch (error) {
      console.error("Combined mood search failed", error);
    } finally {
      setShowLoader(false);
    }
  };

  useEffect(() => {
    if (isLoadingExperiences || isLoadingHolidayPackages || isLoadingProperties) {
      setShowLoader(true);
    } else {
      setShowLoader(false);
    }
  }, [isLoadingExperiences, isLoadingHolidayPackages, isLoadingProperties]);

  useEffect(() => {
    if (isSuccessProperties || isSuccessExperiences || isSuccessHolidayPackages) {
      setIsSuccess(true);
    } else {
      setIsSuccess(false);
    }
  }, [isSuccessProperties, isSuccessExperiences, isSuccessHolidayPackages]);

  useEffect(() => {
    if (!isFetchingProperties) {
      const timer = setTimeout(() => setShowLoader(false), 500); // 300ms di ritardo
      return () => clearTimeout(timer);
    } else {
      setShowLoader(true);
    }
  }, [isFetchingProperties]);

  useEffect(() => {
    if (!isFetchingHolidayPackages) {
      const timer = setTimeout(() => setShowLoader(false), 500); // 300ms di ritardo
      return () => clearTimeout(timer);
    } else {
      setShowLoader(true);
    }
  }, [isFetchingHolidayPackages]);

  useEffect(() => {
    if (!isFetchingExperiences) {
      const timer = setTimeout(() => setShowLoader(false), 500); // 300ms di ritardo
      return () => clearTimeout(timer);
    } else {
      setShowLoader(true);
    }
  }, [isFetchingExperiences]);

  useEffect(() => {
    const isMoodOnly = mood_q && !lat_q && !lng_q && searchType_q === 'all';
    if (isMoodOnly) {
      handleCombinedMoodSearch();
    } else {
      // Reset combined mood search state when not in mood-only mode
      setIsCombinedMoodSearch(false);
      setMoodSearchResults({ stays: [], experiences: [], packages: [] });
      handleSearch();
    }
  }, [mood_q, lat_q, lng_q, searchType_q]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      setIsCombinedMoodSearch(false);
      setMoodSearchResults({ stays: [], experiences: [], packages: [] });
    };
  }, []);

  if (!ready) return null; // Wait for i18next to be ready

  return (
    <div
      className={`relative ${className}`}
      data-nc-id="ListingStayMapPage"
    >
      <Helmet>
        <title>TRIBYOU</title>
      </Helmet>

      <BgGlassmorphism />


      <div className="container md:pt-10 pb-24 lg:pt-16 lg:pb-28" >
        {
          !isSmallDevice &&
          <SectionHeroArchivePage
            defaultType={stockType}
            className="pt-10 pb-24 lg:pb-28 lg:pt-16 "
            handleSearch={handleSearchManual}
          />
        }
      </div>

      {/* SECTION */}
      <div className="container lg:pb-28 2xl:pl-10 xl:pr-0 xl:max-w-none" ref={searchResultRef}>
        {
          isSmallDevice && !searchResults && isSuccess && (
            <div className="flex flex-col items-center justify-center space-y-8 py-24 lg:py-28">
              <Lottie options={{
                loop: true,
                autoplay: true,
                animationData: EmptyAnimation,
                rendererSettings: {
                  preserveAspectRatio: 'xMidYMid slice'
                }
              }}
                height={200}
                width={200}
                isStopped={false}
                isPaused={false} />

              <h2 className="text-2xl font-semibold text-neutral-900 dark:text-neutral-100">{t("translation:search.common.noResultsFound")}</h2>
            </div>
          )
        }
        {
          searchResults && isSmallDevice && (
            <div className="pb-24 lg:pb-28">
              {renderSearchResults(true)}
            </div>
          )
        }
        {
          !isSmallDevice && isSuccess && (
            <div className="pb-24 lg:pb-28">
              {renderSearchResults(false)}
            </div>
          )
        }
      </div>

      <div className="container overflow-hidden mb-[5rem]">
        {/* SECTION 1 */}
        <div className="relative py-16">
          <BackgroundSection />
          <SectionSliderNewCategories
            heading="Le destinazioni più belle d'Italia"
            subHeading="Scopri le destinazione più belle d'Italia presenti su Tribyou"
            categoryCardType="card5"
            itemPerRow={5}
            sliderStyle="style2"
            uniqueClassName="ListingStayMapPage"
          />
        </div>
      </div>
    </div>
  );
};

export default SearchPage;
